import React, { useEffect, useState } from 'react';
import { Button, Card, Form, Input, Modal, Popconfirm, Select, Space, Switch, Tag, message } from 'antd';
import AdaptiveTable from '../components/AdaptiveTable';
import { llmProviderApi, type LlmProviderDTO } from '../api/kfc';
import { clientTablePagination } from '../utils/tablePagination';

const PROVIDER_PRESETS: Record<string, Partial<LlmProviderDTO>> = {
  MOONSHOT: {
    name: 'Kimi K3',
    modelId: 'kimi-k3',
    apiBase: 'https://api.moonshot.cn/v1',
    supportsVision: true,
    reasoningEffort: 'high',
  },
  DEEPSEEK: {
    name: 'DeepSeek V4 Pro',
    modelId: 'deepseek-chat',
    apiBase: 'https://api.deepseek.com',
    supportsVision: false,
    reasoningEffort: '',
  },
};

const REASONING_OPTIONS = [
  { value: '', label: '不设置（模型默认）' },
  { value: 'low', label: 'low（更快更省）' },
  { value: 'high', label: 'high（推荐）' },
  { value: 'max', label: 'max（最准、最慢）' },
];

const LlmProviderManage: React.FC = () => {
  const [list, setList] = useState<LlmProviderDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form] = Form.useForm();

  const load = () => {
    setLoading(true);
    llmProviderApi
      .list()
      .then((res) => setList(res.data || []))
      .catch((e) => {
        setList([]);
        message.error(e.response?.data?.message || '无法加载大模型列表');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const openAdd = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({ providerType: 'MOONSHOT', ...PROVIDER_PRESETS.MOONSHOT });
    setModalOpen(true);
  };

  const onProviderTypeChange = (value: string) => {
    if (editingId) return;
    const preset = PROVIDER_PRESETS[value];
    if (preset) form.setFieldsValue(preset);
  };

  const openEdit = (r: LlmProviderDTO) => {
    setEditingId(r.id);
    form.setFieldsValue({
      name: r.name,
      providerType: r.providerType || 'DEEPSEEK',
      modelId: r.modelId,
      apiBase: r.apiBase,
      supportsVision: !!r.supportsVision,
      reasoningEffort: r.reasoningEffort || '',
      apiKey: undefined,
      apiSecret: undefined,
    });
    setModalOpen(true);
  };

  const onFinish = (values: Record<string, unknown>) => {
    const payload: Partial<LlmProviderDTO> = {
      name: values.name as string,
      providerType: (values.providerType as string) || 'DEEPSEEK',
      modelId: values.modelId as string | undefined,
      apiBase: values.apiBase as string | undefined,
      supportsVision: !!values.supportsVision,
      reasoningEffort: ((values.reasoningEffort as string) || '') as LlmProviderDTO['reasoningEffort'],
    };
    if (values.apiKey) payload.apiKey = values.apiKey as string;
    if (values.apiSecret) payload.apiSecret = values.apiSecret as string;

    const req = editingId
      ? llmProviderApi.update(editingId, payload)
      : llmProviderApi.create({ ...payload, apiKey: (values.apiKey as string) || '' });

    req
      .then(() => {
        message.success(editingId ? '更新成功' : '创建成功');
        setModalOpen(false);
        load();
      })
      .catch((e) => message.error(e.response?.data?.message || '操作失败'));
  };

  const onEnable = (id: string) => {
    llmProviderApi
      .enable(id)
      .then(() => {
        message.success('已启用（其余大模型已取消启用）');
        load();
      })
      .catch((e) => message.error(e.response?.data?.message || '启用失败'));
  };

  const onDelete = (id: string) => {
    llmProviderApi
      .delete(id)
      .then(() => {
        message.success('已删除');
        load();
      })
      .catch((e) => message.error(e.response?.data?.message || '删除失败'));
  };

  return (
    <>
      <Card title="大模型" extra={<Button type="primary" onClick={openAdd}>新增大模型</Button>}>
        <p style={{ marginBottom: 16, color: '#666' }}>
          用于计费重规则解析与测量数据解析（手动上传测量数据需支持图片）。全局只能启用一个；推荐 Kimi K3（Moonshot，原生支持文本与图片）。
        </p>
        <AdaptiveTable
          loading={loading}
          dataSource={list}
          rowKey="id"
          pagination={clientTablePagination()}
          columns={[
            { title: '名称', dataIndex: 'name', width: 180 },
            { title: '厂商', dataIndex: 'providerType', width: 100 },
            { title: 'Model', dataIndex: 'modelId', width: 140, ellipsis: true },
            {
              title: '支持图片',
              dataIndex: 'supportsVision',
              width: 90,
              render: (v: boolean) => (v ? <Tag color="blue">是</Tag> : <Tag>否</Tag>),
            },
            { title: '推理强度', dataIndex: 'reasoningEffort', width: 90, render: (v?: string) => v || '-' },
            { title: 'API Key', dataIndex: 'apiKey', width: 120, ellipsis: true },
            {
              title: '启用',
              dataIndex: 'enabled',
              width: 80,
              render: (v: boolean) => (v ? <Tag color="green">启用中</Tag> : <Tag>未启用</Tag>),
            },
            {
              title: '操作',
              width: 220,
              render: (_, r) => (
                <Space wrap>
                  <Button type="link" size="small" onClick={() => openEdit(r)}>
                    编辑
                  </Button>
                  {!r.enabled && (
                    <Button type="link" size="small" onClick={() => onEnable(r.id)}>
                      启用
                    </Button>
                  )}
                  <Popconfirm title="确定删除？" onConfirm={() => onDelete(r.id)}>
                    <Button type="link" size="small" danger>
                      删除
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </Card>
      <Modal
        title={editingId ? '编辑大模型' : '新增大模型'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        destroyOnClose
        width={520}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="Kimi K3" />
          </Form.Item>
          <Form.Item name="providerType" label="厂商" initialValue="MOONSHOT">
            <Select
              onChange={onProviderTypeChange}
              options={[
                { value: 'MOONSHOT', label: 'MOONSHOT（Kimi）' },
                { value: 'DEEPSEEK', label: 'DEEPSEEK' },
              ]}
            />
          </Form.Item>
          <Form.Item name="modelId" label="Model ID">
            <Input placeholder="kimi-k3" />
          </Form.Item>
          <Form.Item
            name="apiBase"
            label="API Base"
            extra="Kimi 国内账号 https://api.moonshot.cn/v1，国际账号 https://api.moonshot.ai/v1"
          >
            <Input placeholder="https://api.moonshot.cn/v1" />
          </Form.Item>
          <Form.Item
            name="supportsVision"
            label="支持图片"
            valuePropName="checked"
            extra="手动上传测量数据带图片时，启用的模型须支持图片"
          >
            <Switch />
          </Form.Item>
          <Form.Item name="reasoningEffort" label="推理强度" extra="仅 Kimi K3 生效（reasoning_effort）">
            <Select options={REASONING_OPTIONS} />
          </Form.Item>
          <Form.Item
            name="apiKey"
            label="API Key"
            rules={editingId ? [] : [{ required: true, message: '请输入 API Key' }]}
            extra={editingId ? '留空表示不修改' : undefined}
          >
            <Input.Password placeholder={editingId ? '留空不修改' : '必填'} />
          </Form.Item>
          <Form.Item name="apiSecret" label="API Secret" extra={editingId ? '留空表示不修改' : '可选'}>
            <Input.Password placeholder={editingId ? '留空不修改' : '可选'} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                保存
              </Button>
              <Button onClick={() => setModalOpen(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default LlmProviderManage;
